import { merge, omit } from "lodash";
import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { currentUserSelector } from "Auth/state";
import { CreateBandModal } from "Bands/containers";
import {
  bandMetaSelector,
  bandSelectorCreator,
  bandSettingsSelector,
  bandUsersSelector,
  dataMetaSelector,
  dispatchGetBandAction,
  dispatchGetMyBandUsers,
  dispatchGetUserBands,
  getBandUsers,
  myBandUsersSelector,
  userBandselector,
} from "Bands/state";
import {
  BAND_ACTIONS,
  BandSettingsSlugs,
  IBand,
  IBandUser,
  IBaseBandSetting,
  IBirthdayEvent,
  IBirthdays,
  IIconBandSetting,
} from "Bands/types";
import { useBandIdFromPath } from "Bands/utils";
import { API_ENDPOINTS } from "shared/constants";
import { LocalStorageService, loggerService } from "shared/services";
import { IAPIState, IDataKeys, ILocalStorageKey } from "shared/types";
import { createNewDate } from "shared/utils";

interface BandContextState {
  bandId: string | null;
  band: IBand | null;
  allBandUsers: IBandUser[];
  bandUsers: IBandUser[];
  myBandUsers: IBandUser[];
  userBands: IBand[];
  eventTypes: IIconBandSetting[];
  ticketAndPassTypes: IBaseBandSetting[];
  infoBlockTypes: IIconBandSetting[];
  memberCategories: IBaseBandSetting[];
  openCreateBandModal: () => void;
  currentUserBandsInfo: IBandUser[] | null;
  getBandUser: (bandId: string) => IBandUser | null;
  getMyBandUser: (bandId: string) => IBandUser | null;
  findBandUser: (bandUserId: string) => IBandUser | undefined;
  findBandUserByUserId: (userId: string) => IBandUser | undefined;
  currentBandUser: IBandUser | null;
  isLoading: boolean;
  birthdays?: IBirthdays;
  getCategory: (categoryId?: string) => any;
}

const initialState: BandContextState = {
  bandId: null,
  band: null,
  allBandUsers: [],
  bandUsers: [],
  userBands: [],
  myBandUsers: [],
  eventTypes: [],
  ticketAndPassTypes: [],
  infoBlockTypes: [],
  memberCategories: [],
  openCreateBandModal: () => {},
  currentUserBandsInfo: null,
  getBandUser: () => null,
  getMyBandUser: () => null,
  findBandUser: () => undefined,
  findBandUserByUserId: () => undefined,
  currentBandUser: null,
  isLoading: false,
  getCategory: () => null,
};

const BandContext = createContext<BandContextState>(initialState);

interface BandProviderProps {
  bandId?: string;
}

export const BandProvider: FC<BandProviderProps> = ({ children, bandId: propsId }) => {
  const dispatch = useDispatch();

  const bandId = useBandIdFromPath() || propsId!;

  const bandSelector = useMemo(() => bandSelectorCreator(bandId || ""), [bandId]);

  const band = useSelector(bandSelector);
  const allBandUsers = useSelector(bandUsersSelector);

  const { api: apiMeta } = useSelector(bandMetaSelector(bandId as string));

  const currentUser = useSelector(currentUserSelector);
  const userBands = useSelector(userBandselector);
  const myBandUsers = useSelector(myBandUsersSelector);

  const [showCreateBandModal, setShowCreateBandModal] = useState(false);

  const eventTypes = useSelector(bandSettingsSelector<IIconBandSetting>(IDataKeys.eventTypes));
  const ticketAndPassTypes = useSelector(
    bandSettingsSelector<IBaseBandSetting>(IDataKeys.eventTicketAndPassTypes)
  );
  const infoBlockTypes = useSelector(
    bandSettingsSelector<IIconBandSetting>(IDataKeys.eventInfoBlockTypes)
  );
  const memberCategories = useSelector(
    bandSettingsSelector<IIconBandSetting>(IDataKeys.memberCategories)
  );

  // fetch user bands when user id changes
  useEffect(() => {
    if (!currentUser?.id) return;

    dispatchGetUserBands(dispatch);
    dispatchGetMyBandUsers(dispatch);
  }, [dispatch, currentUser?.id]);

  const fetchedBandId = useRef<string>();

  useEffect(() => {
    if (bandId && bandId !== fetchedBandId.current) {
      // when selected band ID changes, fetch more info about the band
      dispatchGetBandAction(dispatch, bandId);
      dispatch(getBandUsers(bandId));
      dispatchGetMyBandUsers(dispatch);

      fetchedBandId.current = bandId;

      // update last band in local storage
      LocalStorageService.setItem(ILocalStorageKey.lastBand, bandId);
    } else {
      // if no band id, remove from local storage
      LocalStorageService.removeItem(ILocalStorageKey.lastBand);
    }
  }, [bandId, dispatch]);

  const { api: createBandApi } = useSelector(dataMetaSelector(BAND_ACTIONS.createBand));

  // If new band created, refetch user bands
  useEffect(() => {
    if (createBandApi?.status === IAPIState.succeeded) {
      dispatchGetUserBands(dispatch);
    }
  }, [createBandApi, dispatch]);

  const bandUsers = useMemo(
    () => allBandUsers.filter(bandUser => !!bandUser.user) || [],
    [allBandUsers]
  );

  const currentUserBandsInfo = useMemo(() => {
    if (!currentUser?.id) return null;

    return userBands.map(band => ({
      band: band.id,
      ...band.users?.find(user => user.user?.id === currentUser.id),
    })) as IBandUser[];
  }, [userBands, currentUser?.id]);

  // const currentMyUserBandsInfo = useMemo(() => {
  //   if (!currentUser?.id) return null;

  //   return myUserBands?.map((band) => ({
  //     band: band.id,
  //     ...band.users?.find((user) => user.user?.id === currentUser.id),
  //   })) as IBandUser[];
  // }, [myUserBands, currentUser?.id]);

  const currentBandUser = useMemo(
    () =>
      currentUser?.id
        ? allBandUsers.find(
            (user: IBandUser) => user.user?.id === API_ENDPOINTS.USER(currentUser?.id)
          ) || null
        : null,
    [allBandUsers, currentUser]
  );

  const getBandUser = useCallback(
    (bandId: string) => currentUserBandsInfo?.find(user => user.band === bandId) || null,
    [currentUserBandsInfo]
  );

  const getMyBandUser = useCallback(
    (bandId: string) =>
      myBandUsers?.find(({ band }) => (band as unknown as IBand).id === bandId) || null,
    [myBandUsers]
  );

  const findBandUser = useCallback(
    (bandUserId: string) =>
      allBandUsers.find(({ id }) => bandUserId === API_ENDPOINTS.BAND_USER(id)),
    [allBandUsers]
  );

  const findBandUserByUserId = useCallback(
    (userId: string) =>
      bandUsers.find(({ user }) => user && user.id === API_ENDPOINTS.USER(userId)),
    [bandUsers]
  );

  // birthdays of selected band
  const bandBirthdays = useMemo(() => {
    let bdaysMap: IBirthdays = {};

    allBandUsers.forEach(bandUser => {
      const birthDate = bandUser.birthDate;

      if (!birthDate) return;

      const bdayDate = createNewDate(birthDate);
      const sameMonthBdays = bdaysMap[String(bdayDate.getMonth())] || [];
      const cleanUser = merge({}, omit(bandUser, "user"), bandUser.user, {
        date: birthDate,
      });

      bdaysMap = merge({}, bdaysMap, {
        [String(bdayDate.getMonth())]: [...sameMonthBdays, cleanUser],
      });
    });

    return bdaysMap;
  }, [allBandUsers]);

  const allBandsBirthdays = useMemo(() => {
    // flat array of all user band users' birthdays
    const birthdays = userBands
      .map((userBand: IBand) =>
        userBand.users?.map(
          userBandUser =>
            merge(
              {},
              omit(userBandUser, ["user", "nickname"]), // remove nickname, because multiple bands possible
              userBandUser.user,
              {
                band: userBand.title,
                date: userBandUser.birthDate,
              }
            ) as IBirthdayEvent
        )
      )
      .flat()
      .filter(bday => !!bday?.date) as IBirthdayEvent[];

    // merge same users in different bands
    let cleanBdays: IBirthdayEvent[] = [];
    let cleandIds: { [key: string]: boolean } = {};

    birthdays.forEach(bday => {
      const bands = birthdays
        .filter(otherBday => otherBday.id === bday.id)
        .map(sameUserBday => sameUserBday.band);

      if (!cleandIds[bday.id]) {
        cleanBdays = [...cleanBdays, merge({}, omit(bday, "band"), { bands })];
        cleandIds = merge({}, cleandIds, { [bday.id]: true });
      }
    });

    // make a bday map with month as key
    let bdaysMap: IBirthdays = {};

    cleanBdays?.forEach((bday: IBirthdayEvent) => {
      const bdayDate = createNewDate(bday.date);
      const sameMonthBdays = bdaysMap[String(bdayDate.getMonth())] || [];

      bdaysMap = merge({}, bdaysMap, {
        [String(bdayDate.getMonth())]: [...sameMonthBdays, bday],
      });
    });

    return bdaysMap;
  }, [userBands]);

  const birthdays = useMemo(() => {
    return bandId ? bandBirthdays : allBandsBirthdays;
  }, [bandId, bandBirthdays, allBandsBirthdays]);

  const getCategory = useCallback(
    (categoryId?: string) => {
      if (!categoryId) return null;

      return memberCategories.find(
        ({ id }) =>
          id === categoryId ||
          id ===
            API_ENDPOINTS.EVENT_SETTING_ITEM(BandSettingsSlugs.band_member_categories, categoryId)
      );
    },
    [memberCategories]
  );

  // ❌ debug
  const renderCount = useRef<number>(0);
  useEffect(() => {
    renderCount.current = renderCount.current + 1;

    loggerService.log({
      icon: "🔄",
      title: "Renders: BAND",
      body: {
        renderCount: renderCount.current,
        band,
      },
    });
  }, [band]);

  return (
    <BandContext.Provider
      value={{
        bandId,
        band,
        allBandUsers,
        bandUsers,
        userBands,
        currentUserBandsInfo,
        eventTypes,
        infoBlockTypes,
        ticketAndPassTypes,
        memberCategories,
        getBandUser,
        findBandUser,
        findBandUserByUserId,
        currentBandUser,
        openCreateBandModal: () => setShowCreateBandModal(true),
        isLoading: apiMeta?.status === IAPIState.pending,
        birthdays,
        getCategory,
        myBandUsers,
        getMyBandUser,
      }}
    >
      {children}
      {showCreateBandModal && <CreateBandModal handleClose={() => setShowCreateBandModal(false)} />}
    </BandContext.Provider>
  );
};

export const useBandContext = () => useContext(BandContext);
